/**
 * Organization List Page
 * 사업자 목록 페이지 - 테이블 형식
 * 정렬: 대기 → 정상 → 비활성
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrganizations, useOwnerActivity } from '../../use-cases';
import {
  Organization,
  getOrganizationStatusText,
  getOrganizationStatusColor,
  isOrganizationPending,
  isOrganizationApproved,
  isOrganizationRejected,
  isOrganizationSuspended,
  formatRelativeTime,
  isInactiveOwner,
  OwnerLastActivity,
} from '../../models';
import { OrganizationApprovalModal } from './OrganizationApprovalModal';
import { OrganizationDetailModal } from './OrganizationDetailModal';
import { CreateOwnerModal } from './CreateOwnerModal';
import { SuspendOwnerModal } from './SuspendOwnerModal';
import { API_BASE_URL } from '../../../../config/api';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const ITEMS_PER_PAGE = 20;

// 조직의 상태 문자열 반환 (정렬/필터용)
const getOrgStatus = (org: Organization): 'pending' | 'approved' | 'rejected' | 'suspended' => {
  if (isOrganizationSuspended(org)) return 'suspended';
  if (isOrganizationApproved(org)) return 'approved';
  if (isOrganizationRejected(org)) return 'rejected';
  return 'pending';
};

// 상태 우선순위 (낮을수록 먼저)
const STATUS_PRIORITY: Record<string, number> = {
  pending: 1,
  approved: 2,
  suspended: 3,
  rejected: 4,
};

export const OrganizationListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const {
    organizations,
    loading,
    error,
    fetchAll,
    approve,
    reject,
    suspend,
    reactivate,
  } = useOrganizations();

  const {
    ownersLastActivity,
    fetchAllOwnersLastActivity,
    getOwnerLastActivity,
  } = useOwnerActivity();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [modalMode, setModalMode] = useState<'approve' | 'reject' | null>(null);
  const [detailOrg, setDetailOrg] = useState<Organization | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [suspendOrg, setSuspendOrg] = useState<Organization | null>(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [memoCounts, setMemoCounts] = useState<Record<string, number>>({});

  // 메모 카운트 조회
  const fetchMemoCounts = useCallback(async (orgIds: string[]) => {
    if (orgIds.length === 0) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/organization-memos/counts/batch?organization_ids=${orgIds.join(',')}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const counts: Record<string, number> = {};
        data.counts.forEach((item: { organization_id: string; count: number }) => {
          counts[item.organization_id] = item.count;
        });
        setMemoCounts(counts);
      }
    } catch (error) {
      console.error('메모 카운트 조회 오류:', error);
    }
  }, []);

  // 초기 데이터 로드 (전체 데이터를 한번에 가져와서 클라이언트에서 필터링)
  useEffect(() => {
    fetchAll();
    fetchAllOwnersLastActivity();
  }, [fetchAll, fetchAllOwnersLastActivity]);

  // 조직 목록 변경시 메모 카운트 조회
  useEffect(() => {
    if (organizations.length > 0) {
      const orgIds = organizations.map((org) => org.id);
      fetchMemoCounts(orgIds);
    }
  }, [organizations, fetchMemoCounts]);

  // 필터링 및 정렬된 데이터
  const processedOrganizations = useMemo(() => {
    let filtered = [...organizations];

    // 상태 필터링
    if (statusFilter !== 'all') {
      filtered = filtered.filter((org) => {
        const status = getOrgStatus(org);
        return status === statusFilter;
      });
    }

    // 검색 필터링
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((org) =>
        org.name.toLowerCase().includes(query) ||
        org.business_number?.includes(query) ||
        org.phone?.includes(query) ||
        org.owner_email?.toLowerCase().includes(query)
      );
    }

    // 상태 우선순위로 정렬 (대기 → 정상 → 비활성)
    filtered.sort((a, b) => {
      const statusA = getOrgStatus(a);
      const statusB = getOrgStatus(b);
      const priorityA = STATUS_PRIORITY[statusA];
      const priorityB = STATUS_PRIORITY[statusB];

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // 같은 우선순위면 최신순
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return filtered;
  }, [organizations, statusFilter, searchQuery]);

  // 페이지네이션
  const totalPages = Math.ceil(processedOrganizations.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return processedOrganizations.slice(start, start + ITEMS_PER_PAGE);
  }, [processedOrganizations, currentPage]);

  // 상태별 카운트
  const statusCounts = useMemo(() => {
    const counts = { all: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 };
    organizations.forEach((org) => {
      counts.all++;
      const status = getOrgStatus(org);
      counts[status]++;
    });
    return counts;
  }, [organizations]);

  const handleStatusFilter = (status: string) => {
    setSearchParams({ status, page: '1' });
  };

  const handlePageChange = (page: number) => {
    setSearchParams({ status: statusFilter, page: String(page) });
  };

  const handleApprove = (org: Organization) => {
    setSelectedOrg(org);
    setModalMode('approve');
  };

  const handleReject = (org: Organization) => {
    setSelectedOrg(org);
    setModalMode('reject');
  };

  const handleModalClose = () => {
    setSelectedOrg(null);
    setModalMode(null);
  };

  const handleModalConfirm = async (reason?: string) => {
    if (!selectedOrg) return;

    let success = false;
    if (modalMode === 'approve') {
      success = await approve(selectedOrg.id, reason);
    } else if (modalMode === 'reject' && reason) {
      success = await reject(selectedOrg.id, reason);
    }

    if (success) {
      handleModalClose();
      fetchAll(); // 새로고침
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // 사용 중지 처리
  const handleSuspend = async (reason: string) => {
    if (!suspendOrg) return;
    setSuspendLoading(true);
    try {
      await suspend(suspendOrg.id, reason);
      setSuspendOrg(null);
      fetchAll();
    } finally {
      setSuspendLoading(false);
    }
  };

  // 재활성화 처리
  const handleReactivate = async (org: Organization) => {
    if (!window.confirm(`'${org.name}' 사업자를 재활성화하시겠습니까?`)) return;

    const success = await reactivate(org.id);
    if (success) {
      fetchAll();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">사업자 관리</h1>
          <p className="text-sm text-gray-600">사업자 신청 승인 및 관리</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2 text-sm"
        >
          <PlusIcon className="h-4 w-4" />
          <span>사업자 추가</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Status Tabs */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'all', label: '전체', count: statusCounts.all },
            { id: 'pending', label: '승인 대기', count: statusCounts.pending },
            { id: 'approved', label: '승인 완료', count: statusCounts.approved },
            { id: 'suspended', label: '사용 중지', count: statusCounts.suspended },
            { id: 'rejected', label: '비활성', count: statusCounts.rejected },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleStatusFilter(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                statusFilter === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                statusFilter === tab.id
                  ? 'bg-indigo-100 text-indigo-600'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="사업자명, 사업자번호, 이메일, 전화번호 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">로딩 중...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    사업자명
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    사업자번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    오너 이메일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    전화번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    신청일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    최근 활동
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    상태
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">
                      {searchQuery ? '검색 결과가 없습니다.' : '데이터가 없습니다.'}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((org) => {
                    const isPending = isOrganizationPending(org);
                    const lastActivity = getOwnerLastActivity(org.owner_id) || getOwnerLastActivity(org.id);
                    const isInactive = isInactiveOwner(lastActivity?.last_activity_at);

                    return (
                      <tr
                        key={org.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setDetailOrg(org)}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center space-x-2">
                            <span>{org.name}</span>
                            {memoCounts[org.id] > 0 && (
                              <span
                                className="inline-flex items-center space-x-0.5 text-indigo-600"
                                title={`내부 메모 ${memoCounts[org.id]}개`}
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                                <span className="text-xs">{memoCounts[org.id]}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {org.business_number || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {org.owner_email || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {org.phone || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(org.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-1.5">
                            {isInactive ? (
                              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" title="7일 이상 미접속" />
                            ) : (
                              <CheckCircleIcon className="h-4 w-4 text-green-500" title="정상 활동" />
                            )}
                            <span className={`text-xs ${isInactive ? 'text-yellow-600' : 'text-green-600'}`}>
                              {formatRelativeTime(lastActivity?.last_activity_at)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOrganizationStatusColor(org)}`}>
                            {getOrganizationStatusText(org)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center space-x-1">
                            {isPending && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(org);
                                  }}
                                  className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                                  title="승인"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReject(org);
                                  }}
                                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="거부"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {/* 승인된 사업자에게 사용 중지 버튼 표시 */}
                            {isOrganizationApproved(org) && !isOrganizationSuspended(org) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSuspendOrg(org);
                                }}
                                className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded"
                                title="사용 중지"
                              >
                                <ExclamationTriangleIcon className="h-4 w-4" />
                              </button>
                            )}
                            {/* 중지된 사업자에게 재활성화 버튼 표시 */}
                            {isOrganizationSuspended(org) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReactivate(org);
                                }}
                                className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                                title="재활성화"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {processedOrganizations.length}개 중 {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
              {Math.min(currentPage * ITEMS_PER_PAGE, processedOrganizations.length)}개
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailOrg && (
        <OrganizationDetailModal
          organization={detailOrg}
          onClose={() => setDetailOrg(null)}
          onDeleted={() => {
            setDetailOrg(null);
            fetchAll();
          }}
        />
      )}

      {/* Approval/Rejection Modal */}
      {selectedOrg && modalMode && (
        <OrganizationApprovalModal
          organization={selectedOrg}
          mode={modalMode}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          loading={loading}
        />
      )}

      {/* Create Owner Modal */}
      {showCreateModal && (
        <CreateOwnerModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            fetchAll();
          }}
        />
      )}

      {/* Suspend Owner Modal */}
      {suspendOrg && (
        <SuspendOwnerModal
          organization={suspendOrg}
          onClose={() => setSuspendOrg(null)}
          onConfirm={handleSuspend}
          loading={suspendLoading}
        />
      )}
    </div>
  );
};
